import React, { useState, useEffect } from "react";
import TimeOffRequest from "../components/TimeOffRequest";
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    AppBar,
    Toolbar,
    Tabs,
    Tab,
    Divider,
    Card,
    CardContent,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from "@mui/material";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import {
    ArrowBack as ArrowBackIcon,
    EventAvailable as EventAvailableIcon,
    History as HistoryIcon,
    CalendarToday as CalendarTodayIcon,
    AccessTime as AccessTimeIcon,
    Edit as EditIcon,
    Block as BlockIcon
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";

const EmployeeTimeOffPage = ({ user, orgId }) => {
    const [tabValue, setTabValue] = useState(0);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentRequest, setCurrentRequest] = useState(null);
    const [editedRequest, setEditedRequest] = useState({
        type: '',
        typeLabel: '',
        startDate: null,
        endDate: null,
        reason: ''
    });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        // Fetch requests when switching to the history tab
        if (newValue === 1) {
            fetchTimeOffRequests();
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        window.location.href = "/login";
    };    

    const handleBack = () => {
        window.location.href = "/employee";
    };

    const fetchTimeOffRequests = async () => {
        if (!user?.uid || !orgId) return;

        setLoading(true);
        setError(null);

        try {
            const requestsRef = collection(db, "organizations", orgId, "timeOffRequests");
            const q = query(
                requestsRef,
                where("userId", "==", user.uid),
                orderBy("requestDate", "desc")
            );

            const snapshot = await getDocs(q);
            const timeOffRequests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore timestamps to JS Date objects
                startDate: doc.data().startDate?.toDate?.() || new Date(doc.data().startDate),
                endDate: doc.data().endDate?.toDate?.() || new Date(doc.data().endDate),
                requestDate: doc.data().requestDate?.toDate?.() || new Date(doc.data().requestDate),
                responseDate: doc.data().responseDate?.toDate?.() ||
                    (doc.data().responseDate ? new Date(doc.data().responseDate) : null)
            }));

            setRequests(timeOffRequests);
        } catch (err) {
            console.error("Error fetching time-off requests:", err);
            setError("Failed to load your time-off request history. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const validateAgainstBlockedDates = async (startDate, endDate, orgId) => {
        try {
            const blockedDatesRef = collection(db, "organizations", orgId, "blockedDates");
            const snapshot = await getDocs(blockedDatesRef);

            const blockedDates = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                startDate: doc.data().startDate?.toDate?.() || new Date(doc.data().startDate),
                endDate: doc.data().endDate?.toDate?.() || new Date(doc.data().endDate)
            }));

            const requestStart = new Date(startDate);
            const requestEnd = new Date(endDate);

            for (const blockedDate of blockedDates) {
                const blockedStart = new Date(blockedDate.startDate);
                const blockedEnd = new Date(blockedDate.endDate);

                // Check if dates overlap
                if (requestStart <= blockedEnd && requestEnd >= blockedStart) {
                    return {
                        isBlocked: true,
                        reason: blockedDate.reason,
                        type: blockedDate.type,
                        dates: `${blockedStart.toLocaleDateString()} - ${blockedEnd.toLocaleDateString()}`
                    };
                }
            }

            return { isBlocked: false };
        } catch (error) {
            console.error("Error checking blocked dates:", error);
            return { isBlocked: false }; // Allow request if check fails
        }
    };

    const handleDeleteRequest = async (requestId) => {
        if (!window.confirm("Are you sure you want to delete this time off request?")) {
            return;
        }

        setLoading(true);
        try {
            const requestRef = doc(db, "organizations", orgId, "timeOffRequests", requestId);
            await deleteDoc(requestRef);

            // Update the local state to remove the deleted request
            setRequests(requests.filter(req => req.id !== requestId));

            // Show success message
            alert("Time off request deleted successfully");
        } catch (err) {
            console.error("Error deleting time-off request:", err);
            alert("Failed to delete time off request. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Edit functionality
    const handleOpenEditDialog = (request) => {
        setCurrentRequest(request);
        setEditedRequest({
            type: request.type || 'vacation',
            typeLabel: request.typeLabel || 'Vacation',
            startDate: request.startDate,
            endDate: request.endDate,
            reason: request.reason || ''
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setCurrentRequest(null);
    };

    const handleEditInputChange = (field, value) => {
        setEditedRequest({
            ...editedRequest,
            [field]: value
        });
    };

    const calculateDuration = (start, end) => {
        if (!start || !end) return 0;

        const startDate = new Date(start);
        const endDate = new Date(end);

        // Reset time to compare just dates
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        // Calculate difference in days (include both start and end dates)
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return diffDays;
    };

    const handleSaveEdit = async () => {
        if (!currentRequest || !editedRequest.startDate || !editedRequest.endDate) {
            alert("Please fill all required fields");
            return;
        }

        setLoading(true);
        try {
            const requestRef = doc(db, "organizations", orgId, "timeOffRequests", currentRequest.id);

            // Calculate duration
            const duration = calculateDuration(editedRequest.startDate, editedRequest.endDate);

            // Update document
            await updateDoc(requestRef, {
                type: editedRequest.type,
                typeLabel: editedRequest.typeLabel,
                startDate: editedRequest.startDate,
                endDate: editedRequest.endDate,
                reason: editedRequest.reason,
                duration: duration,
                updated: new Date()
            });

            // Update local state
            const updatedRequests = requests.map(req =>
                req.id === currentRequest.id
                    ? {
                        ...req,
                        type: editedRequest.type,
                        typeLabel: editedRequest.typeLabel,
                        startDate: editedRequest.startDate,
                        endDate: editedRequest.endDate,
                        reason: editedRequest.reason,
                        duration: duration
                    }
                    : req
            );

            setRequests(updatedRequests);
            handleCloseEditDialog();
            alert("Time off request updated successfully");
        } catch (err) {
            console.error("Error updating time-off request:", err);
            alert("Failed to update time off request. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Format date to display in a user-friendly way
    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get status chip color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Leave type options
    const leaveTypes = [
        { value: 'vacation', label: 'Vacation' },
        { value: 'sick', label: 'Sick Leave' },
        { value: 'personal', label: 'Personal' },
        { value: 'bereavement', label: 'Bereavement' },
        { value: 'jury', label: 'Jury Duty' },
        { value: 'other', label: 'Other' }
    ];

    // Find type label by value
    const getTypeLabel = (typeValue) => {
        const type = leaveTypes.find(t => t.value === typeValue);
        return type ? type.label : 'Vacation';
    };    

    // Initial fetch when component mounts
    useEffect(() => {
        if (tabValue === 1) {
            fetchTimeOffRequests();
        }
    }, [user, orgId, tabValue]);

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Button
                        color="inherit"
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mr: 2 }}
                    >
                        Back to Dashboard
                    </Button>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md">
                <Paper sx={{ mt: 5, p: 4, borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <EventAvailableIcon fontSize="large" sx={{ mr: 2, color: 'primary.main' }} />
                        <Typography variant="h4" component="h1">
                            Time Off Management
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{ mb: 4 }}
                        variant="fullWidth"
                    >
                        <Tab
                            label="Request Time Off"
                            icon={<EventAvailableIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Request History"
                            icon={<HistoryIcon />}
                            iconPosition="start"
                        />
                    </Tabs>

                    {tabValue === 0 && (
                        <Box>
                            <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Need time off?
                                    </Typography>
                                    <Typography variant="body1">
                                        Submit your request below and your manager will be notified.
                                        Please provide as much notice as possible for time off requests.
                                    </Typography>
                                </CardContent>
                            </Card>

                            <TimeOffRequest
                                orgId={orgId}
                                userId={user?.uid}
                                userEmail={user?.email}
                            />
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Your Time Off Request History
                            </Typography>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : error ? (
                                <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                            ) : requests.length === 0 ? (
                                <Alert severity="info" sx={{ my: 2 }}>
                                    You haven't made any time off requests yet.
                                </Alert>
                            ) : (
                                <Grid container spacing={3}>
                                    {requests.map((request) => (
                                        <Grid item xs={12} key={request.id}>
                                            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box>
                                                        <Typography variant="h6">
                                                            {request.typeLabel || request.type} Leave
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                            <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                                            <Typography variant="body1">
                                                                {formatDate(request.startDate)} to {formatDate(request.endDate)}
                                                                {request.duration && (
                                                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                                        ({request.duration} day{request.duration !== 1 ? 's' : ''})
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                        {request.reason && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                Reason: {request.reason}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                Requested on {formatDate(request.requestDate)}
                                                            </Typography>
                                                        </Box>
                                                        {request.responseDate && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                                                Reviewed on {formatDate(request.responseDate)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Chip
                                                            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                                            color={getStatusColor(request.status)}
                                                            sx={{ fontWeight: 'bold', mb: 2 }}
                                                        />

                                                        {/* Edit button only for pending requests */}
                                                        {request.status === 'pending' && (
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => handleOpenEditDialog(request)}
                                                                startIcon={<EditIcon />}
                                                                sx={{ mb: 1 }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        )}

                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleDeleteRequest(request.id)}
                                                            disabled={loading}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<HistoryIcon />}
                                    onClick={fetchTimeOffRequests}
                                    disabled={loading}
                                >
                                    Refresh History
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Time Off Request</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="type-select-label">Leave Type</InputLabel>
                        <Select
                            labelId="type-select-label"
                            value={editedRequest.type}
                            onChange={(e) => {
                                const typeValue = e.target.value;
                                handleEditInputChange('type', typeValue);
                                handleEditInputChange('typeLabel', getTypeLabel(typeValue));
                            }}
                            label="Leave Type"
                        >
                            {leaveTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={editedRequest.startDate ? new Date(editedRequest.startDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleEditInputChange('startDate', new Date(e.target.value))}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="End Date"
                            type="date"
                            value={editedRequest.endDate ? new Date(editedRequest.endDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleEditInputChange('endDate', new Date(e.target.value))}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: editedRequest.startDate ? new Date(editedRequest.startDate).toISOString().split('T')[0] : '',
                            }}
                        />
                    </Box>

                    <TextField
                        label="Reason (Optional)"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                        value={editedRequest.reason}
                        onChange={(e) => handleEditInputChange('reason', e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSaveEdit} color="primary" variant="contained">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EmployeeTimeOffPage;